import director from "director";
import App from "viewModels/App";
import routeService from "services/routeService";
import authService from "services/authService";
import navHelper from "services/navHelper";
import toastService from "services/toastService";
import { hasPermissionsToViewJourneyGadget } from "./util/journeyUtils";

const DirectorRouter = director.Router;

type Authorization = string;
interface AuthorizationRequirement {
    any?: Authorization[];
    all?: Authorization[];
    ignoreDivisions?: boolean;
}

interface RouteDefinition {
    redirectPath?: string;
    auth?: AuthorizationRequirement;
    handler: director.RouteHandler;
    after?: () => void;
}

const redirectToInteractionsSearch = () =>
    navHelper.redirectTo("#/analytics/interactions");

const redirectToInteractionsSearchRoute = {
    handler: redirectToInteractionsSearch,
};

export type RouteDefinitionBuilder = (
    app: App
) => Record<string, RouteDefinition>;
export const buildRouteDefinitions: RouteDefinitionBuilder = (app: App) => ({
    "/": redirectToInteractionsSearchRoute,
    "/admin": redirectToInteractionsSearchRoute,
    "/admin/": redirectToInteractionsSearchRoute,
    "/agent": redirectToInteractionsSearchRoute,
    "/agent/": redirectToInteractionsSearchRoute,

    "/admin/:conversationId": {
        handler(conversationId: string) {
            app.activateAdminPlaybackView(conversationId).activateDefaultTab();
        },
    },
    "/admin/:conversationId/details": {
        handler(conversationId: string) {
            app.activateAdminPlaybackView(
                conversationId
            ).activateConversationDetailsTab();
        },
    },
    "/admin/:conversationId/timeline": {
        handler(conversationId: string) {
            app.activateAdminPlaybackView(conversationId).activateTimelineTab();
        },
    },
    "/admin/:conversationId/qualitySummary": {
        auth: {
            any: [
                "role:qualityAdmin",
                "perm:quality:evaluation:add",
                "perm:quality:evaluation:delete",
                "perm:quality:calibration:add",
                "perm:quality:summary:view",
                "perm:quality:survey:view",
            ],
            ignoreDivisions: true,
        },
        handler(conversationId: string) {
            app.activateAdminPlaybackView(
                conversationId
            ).activateQualitySummaryTab();
        },
    },
    "/admin/:conversationId/qualitySummary/evaluation/new": {
        auth: {
            any: [
                "role:qualityAdmin",
                "perm:quality:evaluation:add",
                "perm:quality:evaluation:delete",
                "perm:quality:calibration:add",
                "perm:quality:summary:view",
                "perm:quality:survey:view",
            ],
            ignoreDivisions: true,
        },
        handler(conversationId: string) {
            app.activateAdminPlaybackView(
                conversationId
            ).activateQualitySummaryTab();
            app.mainView().activateCreateEvaluationView();
        },
        after() {
            app.mainView().deactivateCreateEvaluationView();
        },
    },
    "/admin/:conversationId/qualitySummary/calibration/new": {
        auth: {
            any: [
                "role:qualityAdmin",
                "perm:quality:evaluation:add",
                "perm:quality:evaluation:delete",
                "perm:quality:calibration:add",
                "perm:quality:summary:view",
                "perm:quality:survey:view",
            ],
            ignoreDivisions: true,
        },
        handler(conversationId: string) {
            app.activateAdminPlaybackView(
                conversationId
            ).activateQualitySummaryTab();
            app.mainView().activateCreateCalibrationView();
        },
        after() {
            app.mainView().deactivateCreateCalibrationView();
        },
    },
    "/admin/:conversationId/audit": {
        handler(conversationId: string) {
            app.activateAdminPlaybackView(conversationId).activateAuditTab();
        },
    },
    "/admin/:conversationId/calibrations/:calibrationId": {
        handler(conversationId: string, calibrationId: string) {
            app.activateAdminPlaybackView(
                conversationId
            ).activateCalibrationTab(calibrationId);
        },
    },
    "/admin/:conversationId/evaluations/new": {
        handler(conversationId: string) {
            navHelper.navigateTo(
                navHelper.asHostHash(
                    `#/admin/${conversationId}/qualitySummary/evaluation/new`
                ),
                undefined,
                false
            );
        },
    },
    "/admin/:conversationId/evaluations/:assignedEvaluationId": {
        handler(conversationId: string, assignedEvaluationId: string) {
            const canViewQualitySummary = authService.hasAuthorization({
                any: [
                    "role:qualityAdmin",
                    "perm:quality:evaluation:add",
                    "perm:quality:evaluation:delete",
                    "perm:quality:calibration:add",
                    "perm:quality:summary:view",
                    "perm:quality:survey:view",
                ],
            });
            const tabToActivate = canViewQualitySummary
                ? "qualitySummary"
                : "details";
            navHelper.navigateTo(
                navHelper.asHostHash(
                    `#/admin/${conversationId}/${tabToActivate}/?evaluationId=${assignedEvaluationId}`
                ),
                undefined,
                false
            );
        },
    },
    "/admin/:conversationId/journeys": {
        handler(conversationId: string) {
            if (hasPermissionsToViewJourneyGadget()) {
                app.activateAdminPlaybackView(
                    conversationId
                ).activateCustomerJourneyTab();
            } else {
                toastService.notifyMajorAlert("error.forbidden");
                navHelper.redirectTo(`#/admin/interactions/${conversationId}`);
            }
        },
    },
    "/admin/:conversationId/journeys/:journeySessionId": {
        handler(conversationId: string, journeySessionId: string) {
            if (hasPermissionsToViewJourneyGadget()) {
                app.activateAdminPlaybackView(
                    conversationId
                ).activateCustomerJourneyTab(journeySessionId);
            } else {
                toastService.notifyMajorAlert("error.forbidden");
                navHelper.redirectTo(`#/admin/interactions/${conversationId}`);
            }
        },
    },
    "/admin/:conversationId/transcript": {
        handler(conversationId: string) {
            app.activateAdminPlaybackView(
                conversationId
            ).activateTranscriptTab();
        },
    },
    "/admin/:conversationId/surveys/:surveyId": {
        auth: {
            any: ["perm:quality:survey:view"],
            ignoreDivisions: true,
        },
        handler(conversationId: string, surveyId: string) {
            navHelper.navigateTo(
                navHelper.asHostHash(
                    `#/admin/${conversationId}/qualitySummary/?surveyId=${surveyId}`
                ),
                undefined,
                false
            );
        },
    },
    "/agent/:conversationId": {
        handler(conversationId: string) {
            app.activateAgentPlaybackView(conversationId).activateDefaultTab();
        },
    },
    "/agent/:conversationId/details": {
        handler(conversationId: string) {
            app.activateAgentPlaybackView(
                conversationId
            ).activateConversationDetailsTab();
        },
    },
    "/agent/:conversationId/timeline": {
        handler(conversationId: string) {
            app.activateAgentPlaybackView(conversationId).activateTimelineTab();
        },
    },
    "/agent/:conversationId/evaluations/:assignedEvaluationId": {
        handler(conversationId: string, assignedEvaluationId: string) {
            navHelper.navigateTo(
                navHelper.asHostHash(
                    `#/agent/${conversationId}/?evaluationId=${assignedEvaluationId}`
                ),
                undefined,
                false
            );
        },
    },
    "/agent/:conversationId/journeys": {
        handler(conversationId: string) {
            if (hasPermissionsToViewJourneyGadget()) {
                app.activateAdminPlaybackView(
                    conversationId
                ).activateCustomerJourneyTab();
            } else {
                toastService.notifyMajorAlert("error.forbidden");
                navHelper.redirectTo(`#/admin/interactions/${conversationId}`);
            }
        },
    },
    "/agent/:conversationId/journeys/:journeySessionId": {
        handler(conversationId: string, journeySessionId: string) {
            if (hasPermissionsToViewJourneyGadget()) {
                app.activateAdminPlaybackView(
                    conversationId
                ).activateCustomerJourneyTab(journeySessionId);
            } else {
                toastService.notifyMajorAlert("error.forbidden");
                navHelper.redirectTo(`#/admin/interactions/${conversationId}`);
            }
        },
    },
    "/agent/:conversationId/transcript": {
        handler(conversationId: string) {
            app.activateAgentPlaybackView(
                conversationId
            ).activateTranscriptTab();
        },
    },
});

// baseline permissions to be checked against all routes in the application.
export const appAuthRequirement: AuthorizationRequirement = {
    any: [
        "role:qualityEvaluator",
        "role:qualityAdmin",
        "perm:conversation:communication:view",
        "perm:quality:evaluation:add",
        "perm:quality:calibration:view",
        "perm:quality:evaluation:editScore",
        "perm:quality:survey:view",
        "perm:recording:recording:view",
        "perm:recording:recordingSegment:view",
        "perm:recording:snippetRecording:view",
        "perm:analytics:conversationdetail:view",
        "perm:analytics:agentconversationdetail:view",
        "perm:recording:screenrecording:view",
    ],
    ignoreDivisions: true,
};

function checkRouteAuthorization(
    routeAuthRequirement?: AuthorizationRequirement,
    redirectPath?: string
) {
    const isAuthorizedForApp = authService.hasAuthorization(appAuthRequirement);
    const isAuthorizedForRoute = routeAuthRequirement
        ? authService.hasAuthorization(routeAuthRequirement)
        : isAuthorizedForApp;
    const isAuthorizedForAppAndRoute =
        isAuthorizedForApp && isAuthorizedForRoute;
    const [
        ,
        // hash
        playbackContext,
        conversationId,
    ] = window.location.hash.split("/");
    const basePath = navHelper.buildInteractionHash(
        playbackContext,
        conversationId
    );
    const unauthorizedRedirectPath =
        isAuthorizedForApp && !isAuthorizedForRoute
            ? basePath
            : redirectPath || "#/analytics/interactions";
    if (!isAuthorizedForAppAndRoute) {
        toastService.notifyMajorAlert("error.forbidden");
        navHelper.redirectTo(unauthorizedRedirectPath);
    }
}

function routeDefinitionToDirectorRouteHandler(
    appRoutes: Record<string, RouteDefinition>
): director.RouteMap {
    return Object.fromEntries(
        Object.entries(appRoutes).map(
            ([path, { handler, auth, redirectPath, after }]) => [
                path,
                {
                    before: checkRouteAuthorization.bind(
                        null,
                        auth,
                        redirectPath
                    ),
                    on: handler,
                    after,
                },
            ]
        )
    );
}

/**
 * @param appRoutes hash path handlers for the application
 * @param notfound the top-level 404 handler
 */
export function createRouter(
    appRoutes: Record<string, RouteDefinition>,
    notfound: () => any
): director.Router {
    // transform our route definitions to match what director expects.
    const directorRoutes = routeDefinitionToDirectorRouteHandler(appRoutes);
    return new DirectorRouter(directorRoutes).configure({
        on() {
            return routeService.route(window.location.hash);
        },
        notfound,
        strict: false,
    });
}

export default createRouter;
